import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageTitle } from "@/components/DashUI";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, UploadCloud } from "lucide-react";

export default function SettingsAdmin() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const set = (k) => (e) => setS({ ...s, [k]: e.target?.value ?? e });
  if (!s) return <p className="text-slate-400">Loading…</p>;

  const save = async () => {
    try {
      const { data } = await api.put("/admin/settings", {
        skool_membership_url: s.skool_membership_url, skool_accelerator_url: s.skool_accelerator_url,
        membership_price: s.membership_price, accelerator_price: s.accelerator_price, logo_url: s.logo_url,
        company_email_from: s.company_email_from, email_reply_to: s.email_reply_to,
        local_ai_enabled: !!s.local_ai_enabled,
        local_ai_url: s.local_ai_url, local_ai_model: s.local_ai_model, local_ai_auth: s.local_ai_auth,
        local_ai_system_prompt: s.local_ai_system_prompt,
      });
      setS(data); toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
  };

  return (
    <div>
      <PageTitle title="Settings" subtitle="Configure Skool links, branding, email, and local AI">
        <Button data-testid="settings-save-btn" onClick={save} className="bg-navy hover:bg-navy/90"><Save className="mr-2 h-4 w-4" /> Save</Button>
      </PageTitle>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h3 className="font-heading font-semibold text-navy">Skool & pricing</h3>
          <div className="mt-4 space-y-4">
            <F label="Skool membership URL ($50/mo)"><Input data-testid="set-skool-membership" value={s.skool_membership_url || ""} onChange={set("skool_membership_url")} /></F>
            <F label="Skool accelerator URL ($2,500)"><Input data-testid="set-skool-accelerator" value={s.skool_accelerator_url || ""} onChange={set("skool_accelerator_url")} /></F>
            <div className="grid gap-4 sm:grid-cols-2">
              <F label="Membership price"><Input data-testid="set-membership-price" value={s.membership_price || ""} onChange={set("membership_price")} /></F>
              <F label="Accelerator price"><Input data-testid="set-accelerator-price" value={s.accelerator_price || ""} onChange={set("accelerator_price")} /></F>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-heading font-semibold text-navy">Branding & email</h3>
          <div className="mt-4 space-y-4">
            <F label="Eagle / Care Placement Pro logo URL">
              <div className="flex gap-2">
                <Input data-testid="set-logo-url" value={s.logo_url || ""} onChange={set("logo_url")} placeholder="Paste image URL to display in the header/footer" />
              </div>
              <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-400"><UploadCloud className="h-3.5 w-3.5" /> Upload your logo to any image host and paste the URL here. Appears platform-wide.</p>
              {s.logo_url && <img src={s.logo_url} alt="logo preview" className="mt-2 h-10 object-contain" />}
            </F>
            <F label="Email sender name (Care Placement Pro / Care Placement Pro Team / Eagle Healthcare Education Team)"><Input data-testid="set-email-from" value={s.company_email_from || ""} onChange={set("company_email_from")} /></F>
            <F label="Reply-to email (owner inbox)"><Input data-testid="set-email-replyto" value={s.email_reply_to || ""} onChange={set("email_reply_to")} /></F>
            <p className="rounded-lg bg-sky p-3 text-xs text-slate-600">Email is delivered via Emergent-managed Resend — no API key needed. The From address is platform-managed and verified; you control the sender display name and reply-to above. Emails fall back to logged/mock mode only if the managed key is removed.</p>
          </div>
        </Card>

        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading font-semibold text-navy">Local AI (DGX Spark)</h3>
              <p className="text-sm text-slate-500">Optional. The platform runs on cloud AI without this.</p>
            </div>
            <Switch data-testid="set-local-ai-enabled" checked={!!s.local_ai_enabled} onCheckedChange={(v) => setS({ ...s, local_ai_enabled: v })} />
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <F label="Local API URL"><Input data-testid="set-local-ai-url" value={s.local_ai_url || ""} onChange={set("local_ai_url")} placeholder="http://dgx-spark:8000/v1" /></F>
            <F label="Model"><Input data-testid="set-local-ai-model" value={s.local_ai_model || ""} onChange={set("local_ai_model")} /></F>
            <F label="Authentication"><Input data-testid="set-local-ai-auth" value={s.local_ai_auth || ""} onChange={set("local_ai_auth")} /></F>
            <F label="System prompt"><Input data-testid="set-local-ai-prompt" value={s.local_ai_system_prompt || ""} onChange={set("local_ai_system_prompt")} /></F>
          </div>
        </Card>
      </div>
    </div>
  );
}
function F({ label, children }) {
  return <div><Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>;
}
